import React from 'react';
import { ResponsiveHeatMap } from '@nivo/heatmap';
import { generateGradientsAndColors, formatLabel, NivoTextTheme } from "./utils";



/**
 *
 * @param data {Array}
 *  Example [
 *   {
 *     id: 'ivv',
 *     data: [
 *       { x: 'ivv', y: 46.461200000000005 },
 *       { x: 'iwf', y: 80.51596000000002 },
 *       { x: 'qqq', y: 54.20602 },
 *       { x: 'vtv', y: 10.29139 }
 *     ]
 *   },
 *   {
 *     id: 'iwf',
 *     data: [
 *       { x: 'ivv', y: 39.61797999999998 },
 *       { x: 'iwf', y: 54.20602 },
 *       { x: 'qqq', y: 86.64439999999996 },
 *       { x: 'vtv', y: 10.330000000000002 }
 *     ]
 *   },
 *   {
 *     id: 'qqq',
 *     data: [...]
 *   }
 * ]
 * @param keys
 * @param precision
 * @param postfix
 * @param minValue
 * @param maxValue
 * @returns {JSX.Element}
 * @constructor
 */
const NivoHeatMap = ({ data, keys, precision = 0, postfix = '%',
                         minValue = -100, maxValue = 100, labelSize=12,
                     labelColor='#fff', showUpperAxis=true, padLeft=30}) => {
  const { gradients, colorMapping } = generateGradientsAndColors(keys);

    const labelIncr = labelSize < 10 ? 2 : 0;
  return (
    <ResponsiveHeatMap
      data={data}
      keys={keys}
      indexBy="id"
      margin={{ top: showUpperAxis ? 5 : 0, right: 5, bottom: 5, left: padLeft }}
      forceSquare={true}
      axisTop={showUpperAxis ? {} : null}
      axisRight={null}
      axisBottom={null}
      opacity={0.9}
      axisLeft={{
        orient: 'left',
        tickSize: 2,
        tickPadding: 2,
        tickRotation: 0,
        legend: 'id',
        legendPosition: 'middle',
        legendOffset: -90
      }}
      cellBorderColor={{ from: 'color', modifiers: [['brighter', 0.4]] }}
      labelTextColor={{
        from: 'color',
        modifiers: [['brighter', 2]],
      }}
      label={(d) => formatLabel(d, precision, postfix)}
      cellShape={(props) => (
        <rect
          {...props}
          style={{
            ...props.style,
            fill: `url(#gradient-${props.key})`,
          }}
        />
      )}
      defs={keys.map((key) => (
        <linearGradient
          key={key}
          id={`gradient-${key}`}
          x1="0"
          y1="0"
          x2="0"
          y2="1"
        >
          <stop offset="0%" stopColor={colorMapping[key]} stopOpacity={0.3} />
          <stop offset="100%" stopColor={colorMapping[key]} stopOpacity={1} />
        </linearGradient>
      ))}
      colors={{ type: 'diverging', scheme: 'blues', divergeAt: 0.5, minValue: minValue, maxValue: maxValue }}
      legends={[
        {
            anchor: 'bottom',
            translateX: 0,
            translateY: 30,
            length: 150, // This controls the width of the color bar
            itemHeight: 20,
            symbolSize: 10,
            direction: 'row',
            justify: false,
            thickness: 12,
            tickPosition: 'after',
            tickSize: 0,
            tickSpacing: 4,
            tickOverlap: false,
            tickFormat: '>-.0s',
            title: 'Value →',
            titleAlign: 'start',
            titleOffset: 4,
        }
      ]}
      animate={true}
      motionConfig="gentle"
      theme={{
          ...NivoTextTheme,
          ...{labels: {text: {fontSize: labelSize, fontWeight: '700'}}},
          ...{axis: {ticks: {text: {fontSize: labelSize + labelIncr, fontWeight: '500'}}}},
    }}
    />
  );
};

export default NivoHeatMap;

export const transformDataForNivoHeatMap = (flatData) => {
  const uniqueX = [...new Set(flatData.map(item => item.x))];
  const uniqueY = [...new Set(flatData.map(item => item.y))];

  const transformedData = uniqueY.map(y => {
    const row = { id: y.toUpperCase(), data: [] };
    uniqueX.forEach(x => {
      const foundItem = flatData.find(item => item.x === x && item.y === y);
      if (foundItem) {
          row.data.push({x: foundItem.x.toUpperCase(), y: foundItem.value});
      }
    });
    return row;
  });

  return transformedData;
};